// src/router/router.js — roteamento por hash com lazy import por rota.
//
// API:
//   import { navigate, registerRoute, startRouter } from '@/router/router.js';
//
//   registerRoute('dashboard', () => import('../modules/dashboard/index.js'));
//   registerRoute('obras', () => import('../modules/obras/index.js'));
//   registerRoute('obra', () => import('../modules/obras/detail.js'));
//
//   startRouter();              // ouve hashchange + roda a rota atual
//   navigate('obras');          // muda hash
//   navigate('obra', { id });   // muda hash com params

const _routes = new Map();
let _currentLoader = null;

/**
 * Registra um carregador (dynamic import) para uma rota.
 * O loader deve retornar uma Promise<module> com export `render(params?)`.
 */
export function registerRoute(page, loader) {
  _routes.set(page, loader);
}

/**
 * Navega para uma rota — atualiza window.location.hash.
 * Sem parâmetros: `#obras`. Com: `#obra?id=abc&tab=visao`.
 */
export function navigate(page, params = {}) {
  const qs = new URLSearchParams(params).toString();
  const hash = qs ? `#${page}?${qs}` : `#${page}`;
  if (location.hash === hash) {
    _runCurrentRoute(); // forçar re-render
  } else {
    location.hash = hash;
  }
}

/**
 * Lê a rota atual do hash. Default: 'dashboard'.
 */
// ── Guarda de corrida (12/07) ──────────────────────────────────────────────
// Um módulo que faz `await` (fetch, IA…) e só então escreve em #main-content pode
// escrever DEPOIS que o usuário já trocou de tela — o Dashboard "voltava" sozinho
// quando o card da AIVA respondia. Cada navegação incrementa a geração; o módulo
// captura a sua no início e checa antes de tocar no DOM.
let _gen = 0;
export function rotaGen() { return _gen; }
export function rotaValida(gen, page) {
  return gen === _gen && (!page || getCurrentRoute().page === page);
}

// Devolve o #main-content APENAS se a rota ainda for a esperada. Módulos que
// escrevem depois de um await recebem null — e o `if (!c) return` que já existe
// neles faz o resto. Sem isto, um fetch lento reescrevia a tela do usuário.
export function mainDe(page) {
  if (page && getCurrentRoute().page !== page) return null;
  return document.getElementById('main-content');
}

export function getCurrentRoute() {
  const raw = location.hash.slice(1); // remove #
  if (!raw) return { page: 'dashboard', params: {} };
  const [path, query = ''] = raw.split('?');
  // A ROTA é só o 1º segmento: '#clientes/<id>' e '#contratos/<id>' são deep-links de
  // detalhe (a listagem abre e o módulo lê o id do hash). Sem esta divisão, o router
  // procurava a rota 'clientes/<id>' inteira, não achava e caía na 404 — o bug do link
  // de cliente/contrato em Contas a Receber/Pagar.
  const [page, sub] = path.split('/');
  const params = Object.fromEntries(new URLSearchParams(query));
  if (sub) params.id = sub;   // disponível para quem preferir params em vez de ler o hash
  return { page, params };
}

/**
 * Inicia o router · ouve hashchange e roda a rota atual.
 */
export function startRouter() {
  window.addEventListener('hashchange', _runCurrentRoute);
  _runCurrentRoute();
}

async function _runCurrentRoute() {
  _gen += 1;   // invalida qualquer render assíncrono da rota anterior
  const { page, params } = getCurrentRoute();

  // Guarda de PLANO (Fase 1): bloqueia rota fora do plano do tenant — impede o bypass
  // por #hash de um módulo que a sidebar esconde. Roda só com tenant hidratado.
  try {
    const [{ pageAllowedByPlan, hasPermission, PAGE_SCOPE }, { tenant }] = await Promise.all([
      import('../lib/permissions.js'),
      import('../context/tenantContext.js'),
    ]);
    if (tenant.isHydrated && !pageAllowedByPlan(page, tenant.config?.plano)) {
      try { const { showToast } = await import('../lib/toast.js'); showToast('Este módulo não está disponível no seu plano.', 'info'); } catch (_) {}
      if ((location.hash || '') !== '#dashboard') location.hash = '#dashboard';
      return;
    }
    // Guarda de PERMISSÃO (matriz fase 2 · emenda 002): rota conhecida exige o escopo
    // do papel — fecha o bypass por URL direta (#usuarios etc.) que a sidebar esconde.
    // Só para páginas mapeadas em PAGE_SCOPE; rotas de detalhe/drawer passam direto.
    if (tenant.isHydrated && PAGE_SCOPE[page] && !hasPermission(PAGE_SCOPE[page])) {
      try { const { showToast } = await import('../lib/toast.js'); showToast('Você não tem permissão para acessar este módulo.', 'info'); } catch (_) {}
      if ((location.hash || '') !== '#dashboard') location.hash = '#dashboard';
      return;
    }
  } catch (_) { /* guarda best-effort: nunca trava a navegação por erro de import */ }

  const loader = _routes.get(page);

  // Limpa a sub-bar global ANTES de carregar — cada módulo seta a sua.
  try {
    const { clearSubBar } = await import('../components/sub-bar.js');
    clearSubBar();
  } catch (_) { /* sub-bar opcional */ }

  // Teardown de drawers/overlays órfãos — evita vazamento visual ao trocar de rota.
  // (drawers usam history.replaceState, que NÃO dispara hashchange, então isto só
  //  roda em navegação real — nunca apaga um drawer recém-aberto.)
  try {
    document.querySelectorAll('#drawer-wrap, .cli-drawer, .cli-drawer-backdrop, .sc-sheet, .sc-overlay, .conc-modal-overlay').forEach((el) => el.remove());
    document.getElementById('uh-overlay')?.classList.remove('is-open');
    document.body.classList.remove('uh-pal-open');
  } catch (_) { /* cleanup best-effort */ }

  if (!loader) {
    console.warn(`[router] rota "${page}" não registrada`);
    return _renderNotFound(page);
  }

  // Loading state · só se demorar > 100ms
  const loadingTimer = setTimeout(() => {
    const main = document.getElementById('main-content');
    if (main) main.innerHTML = '<div class="app-loading">Carregando…</div>';
  }, 100);

  try {
    const mod = await loader();
    clearTimeout(loadingTimer);
    if (typeof mod.render !== 'function') {
      throw new Error(`módulo da rota "${page}" não exporta render()`);
    }
    _currentLoader = loader;
    await mod.render(params);
  } catch (err) {
    clearTimeout(loadingTimer);
    console.error(`[router] erro ao carregar rota "${page}":`, err);
    const main = document.getElementById('main-content');
    if (main) {
      main.innerHTML = `<div class="empty-state" style="padding:40px;color:var(--red)">
        Erro ao carregar página: ${err.message}
      </div>`;
    }
  }
}

function _renderNotFound(page) {
  const main = document.getElementById('main-content');
  if (!main) return;
  // Padrao Empty do shadcn: ícone tonal, título, descrição e ação. Sem emoji, com tokens
  // do tema (o emoji antigo ignorava claro/escuro e a mensagem exibia o caminho cru).
  var dark = !document.documentElement.classList.contains('light');
  var ico = '<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/><path d="M11 8v3"/><path d="M11 14h.01"/></svg>';
  var seta = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>';
  main.innerHTML =
    '<div class="sc-scope' + (dark ? ' dark' : '') + '" style="display:flex;align-items:center;justify-content:center;min-height:60vh;padding:40px">' +
      '<div style="max-width:420px;text-align:center;color:var(--foreground)">' +
        '<div style="width:64px;height:64px;margin:0 auto 20px;border-radius:var(--radius-lg);display:grid;place-items:center;background:var(--muted);color:var(--muted-foreground)">' + ico + '</div>' +
        '<h2 style="font:600 18px/24px inherit;margin:0 0 6px">Página não encontrada</h2>' +
        '<p style="font:400 14px/20px inherit;color:var(--muted-foreground);margin:0 0 20px">Este item pode ter sido movido, ou o endereço não existe.</p>' +
        '<button class="sc-btn" onclick="location.hash=\'#dashboard\'" style="display:inline-flex;align-items:center;gap:8px;height:40px;padding:0 16px;border-radius:var(--radius-md);border:1px solid var(--border);background:var(--card);color:var(--foreground);cursor:pointer;font:500 14px/1 inherit">' + seta + ' Voltar ao início</button>' +
      '</div>' +
    '</div>';
}
